import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { copyToClipboard } from '../lib/utils';
import { 
  Plus, 
  Globe, 
  FileText, 
  MoreVertical, 
  Trash2,
  Loader2,
  FolderOpen,
  ArrowRight,
  CheckCircle2,
  Clock,
  AlertCircle,
  AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AssetsPage() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAssetName, setNewAssetName] = useState('');
  const [newAssetUrl, setNewAssetUrl] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // Asset to delete
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      const response = await axios.get(`${API}/assets`);
      setAssets(response.data);
    } catch (error) {
      toast.error('Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    if (!newAssetName.trim() || !newAssetUrl.trim()) {
      toast.error('Please enter both name and URL');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/assets`, {
        name: newAssetName,
        url: newAssetUrl
      });
      toast.success('Asset created! Starting website scan...');
      setShowCreateModal(false);
      setNewAssetName('');
      setNewAssetUrl('');
      navigate(`/assets/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to create asset');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteAsset = async (assetId, e) => {
    e.stopPropagation();
    // Find the asset to show in confirmation dialog
    const assetToDelete = assets.find(a => a.id === assetId);
    setDeleteConfirm(assetToDelete);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/assets/${deleteConfirm.id}`);
      toast.success('Asset deleted');
      setDeleteConfirm(null);
      fetchAssets();
    } catch (error) {
      toast.error('Failed to delete asset');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ready':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'scraping':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'ready':
        return 'Ready';
      case 'scraping':
        return 'Scanning...';
      case 'error':
        return 'Error';
      default:
        return 'Draft';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-surface/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-2xl font-bold text-primary"
            >
              Pravik
            </button>
            <span className="text-muted-foreground">/</span>
            <h1 className="text-xl font-semibold text-foreground">Assets</h1>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-primary text-white hover:bg-primary/90 rounded-full px-6"
            data-testid="create-asset-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Asset
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Intro Section */}
        <div className="mb-8 p-6 bg-gradient-to-r from-primary/5 to-primary/10 rounded-2xl border border-primary/20">
          <h2 className="text-lg font-semibold text-foreground mb-2">What are Assets?</h2>
          <p className="text-muted-foreground">
            Assets are reusable website profiles that include scraped content, messaging policies, and company knowledge. 
            Create campaigns from assets to ensure consistent messaging across all your marketing efforts.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : assets.length === 0 ? (
          <div className="text-center py-20">
            <FolderOpen className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No assets yet</h3>
            <p className="text-muted-foreground mb-6">Create your first asset to store website context and policies</p>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-primary text-white hover:bg-primary/90 rounded-full px-8"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Asset
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {assets.map((asset) => (
              <div
                key={asset.id}
                onClick={() => navigate(`/assets/${asset.id}`)}
                className="group bg-surface border border-border rounded-2xl p-6 hover:border-primary/50 hover:shadow-lg transition-all cursor-pointer"
                data-testid={`asset-card-${asset.id}`}
              >
                {/* Asset Slug at the top */}
                {asset.asset_slug && (
                  <div className="mb-3 -mt-1">
                    <code 
                      className="text-xs font-mono bg-muted/50 text-muted-foreground px-2 py-1 rounded-md border border-border/50 cursor-pointer hover:bg-muted"
                      onClick={async (e) => {
                        e.stopPropagation();
                        const success = await copyToClipboard(asset.asset_slug);
                        if (success) {
                          toast.success('Asset ID copied!');
                        }
                      }}
                      title="Click to copy Asset ID"
                    >
                      {asset.asset_slug}
                    </code>
                  </div>
                )}
                
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Globe className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${
                      asset.status === 'ready' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                      asset.status === 'scraping' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                      asset.status === 'error' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                      'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                    }`}>
                      {getStatusIcon(asset.status)}
                      {getStatusText(asset.status)}
                    </span>
                    <button
                      onClick={(e) => handleDeleteAsset(asset.id, e)}
                      className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 text-muted-foreground hover:text-red-600 md:opacity-0 md:group-hover:opacity-100 transition-all"
                      data-testid={`delete-asset-${asset.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <h3 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
                  {asset.name}
                </h3>
                <p className="text-sm text-muted-foreground truncate mb-4">
                  {asset.url}
                </p>

                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  {asset.policy && (
                    <span className="flex items-center gap-1">
                      <FileText className="w-3.5 h-3.5" />
                      Has Policy
                    </span>
                  )}
                  <span>
                    {new Date(asset.created_at).toLocaleDateString()}
                  </span>
                </div>

                <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Click to manage</span>
                  <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-md border border-border shadow-2xl relative z-[101]">
            <h2 className="text-xl font-semibold text-foreground mb-4">Create New Asset</h2>
            <form onSubmit={handleCreateAsset}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Asset Name
                  </label>
                  <Input
                    type="text"
                    value={newAssetName}
                    onChange={(e) => setNewAssetName(e.target.value)}
                    placeholder="e.g., My Company Website"
                    className="w-full"
                    data-testid="asset-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Website URL
                  </label>
                  <Input
                    type="url"
                    value={newAssetUrl}
                    onChange={(e) => setNewAssetUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="w-full"
                    data-testid="asset-url-input"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={creating}
                  className="flex-1 bg-primary text-white hover:bg-primary/90 rounded-full"
                  data-testid="create-asset-submit"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Asset'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-md border border-border shadow-2xl relative z-[101]">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Delete Asset</h2>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-foreground mb-2">
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              All campaigns using this asset will lose their source data.
            </p>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 rounded-full"
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 bg-red-600 text-white hover:bg-red-700 rounded-full"
                data-testid="confirm-delete-asset"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Asset
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
